import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { createConsultation } from '../../api/clinical'
import { getPatient } from '../../api/patients'
import { getMyPractices } from '../../api/staff'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../context/AuthContext'
import type { MyPractice } from '../../types/clinical'

export default function ConsultationForm() {
  const { id: patientIdStr } = useParams<{ id: string }>()
  const patientId = Number(patientIdStr)
  const [searchParams] = useSearchParams()
  const appointmentId = searchParams.get('appointment_id') ? Number(searchParams.get('appointment_id')) : null
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  const [patientName, setPatientName] = useState('')
  const [practices, setPractices] = useState<MyPractice[]>([])
  const [practiceId, setPracticeId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'practiceId' | 'date', string>>>({})

  useEffect(() => {
    Promise.all([getPatient(patientId), getMyPractices()])
      .then(([patient, myPractices]) => {
        setPatientName(patient.name)
        setPractices(myPractices)
        if (myPractices.length === 1) setPracticeId(String(myPractices[0].id))
      })
      .catch(() => setError('Failed to load form data.'))
      .finally(() => setLoading(false))
  }, [patientId])

  const validate = (): boolean => {
    const errs: typeof fieldErrors = {}
    if (!practiceId) errs.practiceId = 'Please select a practice.'
    if (!date) errs.date = 'Date is required.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !validate()) return
    setSubmitting(true)
    setError(null)
    try {
      const consultation = await createConsultation(patientId, {
        patient_id: patientId,
        practice_id: Number(practiceId),
        appointment_id: appointmentId,
        date,
        reason: reason.trim() || null,
      })
      toast.success('Consultation created.')
      void navigate(`/consultations/${consultation.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  if (user?.role === 'org_admin') {
    return (
      <div className="p-8">
        <ErrorBox>Organization admins don't have access to clinical documentation.</ErrorBox>
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate(-1)}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">
          New Consultation {patientName ? `— ${patientName}` : ''}
        </h1>
      </div>

      <Card>
        <SectionTitle>Details</SectionTitle>
        {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <FormField label="Practice" required error={fieldErrors.practiceId}>
            <select
              value={practiceId}
              onChange={(e) => { setPracticeId(e.target.value); setFieldErrors(p => ({ ...p, practiceId: undefined })) }}
              className={selectClass(!!fieldErrors.practiceId)}
            >
              <option value="">Select practice…</option>
              {practices.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </FormField>

          <FormField label="Date of visit" required error={fieldErrors.date}>
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setFieldErrors(p => ({ ...p, date: undefined })) }}
              className={inputClass(!!fieldErrors.date)}
            />
          </FormField>

          <FormField label="Chief complaint / reason" hint="Optional — can be added or edited later">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Annual check-up, limping on right foreleg…"
              className={inputClass()}
            />
          </FormField>

          <div className="flex gap-3 mt-7 pt-6 border-t border-border">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create & Open Record'}
            </Button>
            <Button type="button" variant="outline" onClick={() => void navigate(-1)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
